import { supabase } from '@/lib/supabase'
import type { Payment } from '@/types/domain'

export const paymentsRepo = {
  async findAll(): Promise<Payment[]> {
    const { data, error } = await supabase
      .from('payments_received')
      .select(`
        *,
        client:client_id(*),
        customer:customer_id(*)
      `)
      .order('payment_date', { ascending: false })

    if (error) throw error
    return (data || []) as Payment[]
  },

  async findByClient(clientId: string): Promise<Payment[]> {
    const { data, error } = await supabase
      .from('payments_received')
      .select(`
        *,
        client:client_id(*),
        customer:customer_id(*)
      `)
      .eq('client_id', clientId)
      .order('payment_date', { ascending: false })

    if (error) throw error
    return (data || []) as Payment[]
  },

  async create(payment: any): Promise<Payment> {
    const { data: { user } } = await supabase.auth.getUser()

    const { data, error } = await supabase
      .from('payments_received')
      .insert({ ...payment, created_by: user?.id } as any)
      .select(`
        *,
        client:client_id(*),
        customer:customer_id(*)
      `)
      .single()

    if (error) {
      console.error('Error creating payment:', error)
      throw error
    }

    // Update invoice amount_paid if payment is applied to invoice(s)
    // Note: balance_due is a GENERATED column (original_amount - amount_paid)
    if (payment.invoice_ids && Array.isArray(payment.invoice_ids) && payment.invoice_ids.length > 0) {
      for (const invoiceId of payment.invoice_ids) {
        // Get current invoice
        const { data: invoice, error: fetchError } = await supabase
          .from('invoices')
          .select('amount_paid, original_amount')
          .eq('id', invoiceId)
          .single()

        if (fetchError) {
          console.error('Error fetching invoice:', fetchError)
          throw new Error(`Failed to fetch invoice ${invoiceId}: ${fetchError.message}`)
        }

        if (invoice) {
          const invoiceData = invoice as any
          // Increase amount_paid (balance_due will auto-calculate as original_amount - amount_paid)
          const newAmountPaid = Math.min(
            invoiceData.original_amount,
            (invoiceData.amount_paid || 0) + payment.amount
          )

          const { error: updateError } = await supabase
            .from('invoices')
            .update({
              amount_paid: newAmountPaid
            } as never)
            .eq('id', invoiceId)

          if (updateError) {
            console.error('Error updating invoice amount_paid:', updateError)
            throw new Error(`Failed to update invoice: ${updateError.message}`)
          }

          // Check if invoice is now fully paid and update status
          const { data: updatedInvoice } = await supabase
            .from('invoices')
            .select('balance_due, status, original_amount')
            .eq('id', invoiceId)
            .single()

          if (updatedInvoice) {
            const invoiceData = updatedInvoice as any
            // If balance is $0 and status is not already 'paid', update it
            if (invoiceData.balance_due === 0 && invoiceData.status !== 'paid') {
              await supabase
                .from('invoices')
                .update({ status: 'paid' } as never)
                .eq('id', invoiceId)
            }
            // If balance is greater than 0 but less than original amount, set to partial_payment
            else if (invoiceData.balance_due > 0 && invoiceData.balance_due < invoiceData.original_amount && invoiceData.status === 'outstanding') {
              await supabase
                .from('invoices')
                .update({ status: 'partial_payment' } as never)
                .eq('id', invoiceId)
            }
          }
        }
      }
    }

    return data as Payment
  },

  async update(id: string, updates: any): Promise<Payment> {
    // Get the original payment to check if invoice_ids or amount changed
    const { data: originalPayment } = await supabase
      .from('payments_received')
      .select('invoice_ids, amount')
      .eq('id', id)
      .single()

    const { data, error } = await supabase
      .from('payments_received')
      .update(updates as never)
      .eq('id', id)
      .select(`
        *,
        client:client_id(*),
        customer:customer_id(*)
      `)
      .single()

    if (error) throw error

    // Handle invoice balance updates if invoice_ids or amount changed
    if (originalPayment) {
      const origPayment = originalPayment as any
      const paymentData = data as any
      const oldInvoiceIds = origPayment.invoice_ids || []
      const newInvoiceIds = updates.invoice_ids || paymentData.invoice_ids || []
      const oldAmount = origPayment.amount
      const newAmount = updates.amount !== undefined ? parseFloat(updates.amount) : paymentData.amount

      // Restore amount_paid for removed invoices
      for (const invoiceId of oldInvoiceIds) {
        if (!newInvoiceIds.includes(invoiceId)) {
          const { data: invoice } = await supabase
            .from('invoices')
            .select('amount_paid')
            .eq('id', invoiceId)
            .single()

          if (invoice) {
            const invoiceData = invoice as any
            // Decrease amount_paid to restore balance
            const restoredAmountPaid = Math.max(0, (invoiceData.amount_paid || 0) - oldAmount)
            await supabase
              .from('invoices')
              .update({
                amount_paid: restoredAmountPaid
              } as never)
              .eq('id', invoiceId)
          }
        }
      }

      // Update amount_paid for new or existing invoices
      for (const invoiceId of newInvoiceIds) {
        const { data: invoice } = await supabase
          .from('invoices')
          .select('amount_paid, original_amount')
          .eq('id', invoiceId)
          .single()

        if (invoice) {
          const invoiceData = invoice as any
          let newAmountPaid = invoiceData.amount_paid || 0

          // If this is an existing invoice, first restore old amount
          if (oldInvoiceIds.includes(invoiceId)) {
            newAmountPaid = Math.max(0, newAmountPaid - oldAmount)
          }

          // Apply new amount
          newAmountPaid = Math.min(invoiceData.original_amount, newAmountPaid + newAmount)

          await supabase
            .from('invoices')
            .update({
              amount_paid: newAmountPaid
            } as never)
            .eq('id', invoiceId)

          // Check if invoice status needs updating
          const { data: updatedInvoice } = await supabase
            .from('invoices')
            .select('balance_due, status, original_amount')
            .eq('id', invoiceId)
            .single()

          if (updatedInvoice) {
            const invoiceData = updatedInvoice as any
            if (invoiceData.balance_due === 0 && invoiceData.status !== 'paid') {
              await supabase
                .from('invoices')
                .update({ status: 'paid' } as never)
                .eq('id', invoiceId)
            } else if (invoiceData.balance_due > 0 && invoiceData.balance_due < invoiceData.original_amount && invoiceData.status === 'outstanding') {
              await supabase
                .from('invoices')
                .update({ status: 'partial_payment' } as never)
                .eq('id', invoiceId)
            } else if (invoiceData.balance_due === invoiceData.original_amount && invoiceData.status === 'partial_payment') {
              await supabase
                .from('invoices')
                .update({ status: 'outstanding' } as never)
                .eq('id', invoiceId)
            }
          }
        }
      }
    }

    return data as Payment
  },

  async delete(id: string): Promise<void> {
    console.log('Deleting payment:', id)

    // Get the payment before deleting to restore invoice balances
    const { data: payment, error: fetchError } = await supabase
      .from('payments_received')
      .select('invoice_ids, amount')
      .eq('id', id)
      .single()

    if (fetchError) {
      console.error('Error fetching payment before delete:', fetchError)
      throw new Error(`Failed to fetch payment: ${fetchError.message}`)
    }

    console.log('Payment data before delete:', payment)

    const { error } = await supabase
      .from('payments_received')
      .delete()
      .eq('id', id)

    if (error) {
      console.error('Error deleting payment:', error)
      throw new Error(`Failed to delete payment: ${error.message}`)
    }

    console.log('Payment deleted successfully')

    // Restore invoice balances by decreasing amount_paid
    if (payment) {
      const paymentData = payment as any
      if (paymentData.invoice_ids && Array.isArray(paymentData.invoice_ids)) {
        console.log('Restoring balances for invoices:', paymentData.invoice_ids)

        for (const invoiceId of paymentData.invoice_ids) {
          const { data: invoice, error: invoiceFetchError } = await supabase
            .from('invoices')
            .select('amount_paid')
            .eq('id', invoiceId)
            .single()

          if (invoiceFetchError) {
            console.error('Error fetching invoice:', invoiceFetchError)
            throw new Error(`Failed to fetch invoice ${invoiceId}: ${invoiceFetchError.message}`)
          }

          if (invoice) {
            const invoiceData = invoice as any
            // Decrease amount_paid to restore balance (balance_due will auto-recalculate)
            const restoredAmountPaid = Math.max(0, (invoiceData.amount_paid || 0) - paymentData.amount)

            console.log(`Restoring invoice ${invoiceId} amount_paid from ${invoiceData.amount_paid} to ${restoredAmountPaid}`)

            const { error: updateError } = await supabase
              .from('invoices')
              .update({
                amount_paid: restoredAmountPaid
              } as never)
              .eq('id', invoiceId)

            if (updateError) {
              console.error('Error restoring invoice amount_paid:', updateError)
              throw new Error(`Failed to restore invoice: ${updateError.message}`)
            }

            // Check if invoice status needs updating after restoring balance
            const { data: updatedInvoice } = await supabase
              .from('invoices')
              .select('balance_due, status, original_amount')
              .eq('id', invoiceId)
              .single()

            if (updatedInvoice) {
              const invoiceData = updatedInvoice as any
              if (invoiceData.balance_due === 0 && invoiceData.status !== 'paid') {
                await supabase
                  .from('invoices')
                  .update({ status: 'paid' } as never)
                  .eq('id', invoiceId)
              } else if (invoiceData.balance_due > 0 && invoiceData.balance_due < invoiceData.original_amount && invoiceData.status !== 'partial_payment') {
                await supabase
                  .from('invoices')
                  .update({ status: 'partial_payment' } as never)
                  .eq('id', invoiceId)
              } else if (invoiceData.balance_due === invoiceData.original_amount && invoiceData.status !== 'outstanding') {
                await supabase
                  .from('invoices')
                  .update({ status: 'outstanding' } as never)
                  .eq('id', invoiceId)
              }
            }
          }
        }

        console.log('Invoice balances restored successfully')
      }
    }
  },
}
